import * as React from 'react';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import * as Yup from 'yup';
import {
	Form, Formik, FormikHelpers, FormikProps,
} from 'formik';

import Button from '@common/react/components/Forms/Button';
import { simpleStringValidator } from '@common/react/utils/validationHelpers';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { BaseUser } from '@common/typescript/objects/BaseUser';
import { BaseApplicationState } from '@common/react/store';
import * as LoginState from '@common/react/store/Login';
import PhoneOrEmailInput, { emailOrPhoneValidator } from '@common/react/components/Forms/PhoneOrEmailInput/PhoneOrEmailInput';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';

interface FormValues {
	login: string;
	password: string;
}

const validationSchema = Yup.object().shape({
	login: emailOrPhoneValidator,
	password: simpleStringValidator,
});

interface LoginProps<User extends BaseUser> {
	onSubmit: (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => void;
	containerClassName?: string;
	loading?: boolean;
	afterBaseRedirectUser?: (user: User) => React.ReactNode;
	renderAuthWithCode?: (user: User, redirectUser: (user: User) => JSX.Element) => React.ReactNode;
	logo?: React.ReactNode;
	error?: string;
	buttons?: React.ReactNode;
	withFieldTitle?: boolean;
	title?: string;
	withoutServerPage?: boolean;
}

const Login: <User extends BaseUser>(p: LoginProps<User>) => React.ReactElement<User> = <User extends BaseUser, >(props) => {
	const search = useLocation().search;

	const {
		afterBaseRedirectUser, onSubmit, loading, renderAuthWithCode, withFieldTitle,
	} = props;
	const {
		containerClassName = '', logo, error, buttons, title = 'Login', withoutServerPage,
	} = props;

	const loginState: LoginState.LoginState<User> | null = useSelector((state: BaseApplicationState<User>) => state.login);
	const hostOptions = useSelector((state: BaseApplicationState<BaseUser>) => state.hostOptions.item);

	const redirectUser = React.useCallback((user: User) => {
		if (hostOptions?.redirectUrlName) {
			const redirectUrl = new URLSearchParams(search).get(hostOptions.redirectUrlName);
			if (redirectUrl) {
				return <Redirect to={redirectUrl} />;
			}
		}

		if (afterBaseRedirectUser) {
			return afterBaseRedirectUser(user);
		}

		return <Redirect to="/dashboard" />;
	}, [afterBaseRedirectUser]);

	return (<>
		{!withoutServerPage
				&& <ServerPageProvider
					path="login"
					defaultTitle="Login"
					loader={<></>}
					inner={(serverPage) => <></>}
				/>
		}
		<div className={containerClassName}>
			{logo}
			<div className="enter-page__container">
				{renderAuthWithCode && loginState?.user
					? renderAuthWithCode(loginState?.user, redirectUser)
					: <div className="enter-page__form general-form">
						{loginState?.user && redirectUser(loginState.user)}
						{title && <div className="site-headline text-center">
							<h1>{title}</h1>
						</div>}
						<Formik
							initialValues={{ login: '', password: '' }}
							onSubmit={onSubmit}
							validationSchema={validationSchema}
						>
							{(formikBag: FormikProps<FormValues>) => (
								<Form>
									<FormikField
										fieldName="login"
										title={withFieldTitle ? 'Email or Phone number*' : ''}
										containerClassName="form-group"
										render={(fieldProps, inputProps) =>
											<PhoneOrEmailInput
												fieldProps={fieldProps}
												className="form-control"
												placeholder={withFieldTitle ? '' : 'Email or Phone number*'}
												{...inputProps}
											/>}
									/>
									<FormikField
										fieldName="password"
										title={withFieldTitle ? 'Password*' : ''}
										inputProps={{ placeholder: withFieldTitle ? '' : 'Password*', type: 'password' }}
										containerClassName="form-group"
									/>
									{error && <div className="alert alert-danger">{error}</div>}
									<div className="clearfix mb10">
										<div className="pull-right">
											<Link to="/recover">Forget Password?</Link>
										</div>
									</div>
									<div className="text-center">
										<Button
											other={{ id: 'login-button' }}
											className="btn btn-primary"
											isLoading={loginState?.isLoading || loading}
										>
											Login
										</Button>
										{buttons && buttons}
									</div>
								</Form>
							)}
						</Formik>
						{loginState.message && <div className="alert alert-danger mt10">{loginState.message}</div>}
					</div>
				}
			</div>
		</div>
	</>
	);
};

export default Login;
